import type { CarePage } from "@/lib/api/care";
import { cn } from "@/lib/utils";
import { Clock, ClockAlert } from "lucide-react";
import type { ReactNode } from "react";
import {
  ASSESSMENT_NAMES,
  CareButton,
  CareCard,
  CodeChip,
  FieldLabel,
  ScoreTag,
  SectionHeader,
  UserChip,
  formatCareDate,
} from "./care-primitives";

interface PrimaryPresentationProps {
  page: CarePage;
  onChangePresentation: () => void;
}

export function PrimaryPresentation({
  page,
  onChangePresentation,
}: PrimaryPresentationProps) {
  const hasSurvey = page.latest_score !== null;

  const billing = page.care_reviews_enabled !== false;

  return (
    <CareCard className="border-[rgba(31,30,29,0.16)]">
      {billing ? (
        <>
          <SectionHeader
            title="Primary presentation"
            muted={page.presentation !== null}
            action={
              <CareButton onClick={onChangePresentation}>
                Change Presentation
              </CareButton>
            }
          />

          {page.presentation ? (
            <div className="flex flex-col items-start">
              <CodeChip code={page.presentation.code} />
              <h2 className="font-heading text-[32px] font-bold leading-10 tracking-[-0.32px] text-text-primary">
                {page.presentation.description}
              </h2>
            </div>
          ) : (
            <h2 className="font-heading text-[32px] font-bold leading-10 tracking-[-0.32px] text-text-primary">
              Not yet assigned
            </h2>
          )}
        </>
      ) : null}

      <div
        className={cn(
          "flex items-start",
          billing && "mt-4 border-t border-[#dfddd8] pt-4",
        )}
      >
        <Stat label="Mentor" className="flex-1">
          {page.mentor ? <UserChip user={page.mentor} linked /> : <Empty />}
        </Stat>
        <Stat label="Days in mentorship">
          <Big>{formatDays(page.days_on_platform)}</Big>
        </Stat>
        <Stat label="Assessment">
          {hasSurvey ? (
            <Big>{ASSESSMENT_NAMES[page.assessment]}</Big>
          ) : (
            <Empty />
          )}
        </Stat>
        <Stat label="Latest score">
          {page.latest_score ? (
            <>
              <ScoreTag
                score={page.latest_score.score}
                severity={page.latest_score.severity}
              />
              <p className="font-body text-xs leading-4 text-[#a0998d]">
                {formatCareDate(page.latest_score.taken_at)}
              </p>
            </>
          ) : (
            <Empty />
          )}
        </Stat>
        {billing ? (
          <Stat label="Next review">
            <NextReview next={page.next_review} />
          </Stat>
        ) : null}
      </div>
    </CareCard>
  );
}

function Stat({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex w-[140px] flex-col items-start gap-1", className)}>
      <FieldLabel>{label}</FieldLabel>
      {children}
    </div>
  );
}

function Big({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "font-heading text-2xl font-bold leading-8 text-text-primary",
        className,
      )}
    >
      {children}
    </p>
  );
}

function Empty() {
  return <Big className="text-[#6e6b67]">None</Big>;
}

function formatDays(days: number) {
  return `${days} ${days === 1 ? "day" : "days"}`;
}

function NextReview({ next }: { next: CarePage["next_review"] }) {
  switch (next.state) {
    case "none":
      return <Empty />;
    case "today":
      return (
        <Big className="flex items-center gap-2 text-[#e18002]">
          <ClockAlert className="size-6" aria-hidden />
          Today
        </Big>
      );
    case "days": {
      const soon = (next.days ?? 0) <= 2;
      return (
        <Big
          className={cn("flex items-center gap-2", soon && "text-[#e18002]")}
        >
          {soon ? (
            <ClockAlert className="size-6" aria-hidden />
          ) : (
            <Clock className="size-6" aria-hidden />
          )}
          {formatDays(next.days ?? 0)}
        </Big>
      );
    }
    case "awaiting_survey":
      return <Big>Awaiting Survey</Big>;
    case "paused":
      return <Big>Paused</Big>;
  }
}
